(() => {
  if (window.__svProductControllerV16) return;
  window.__svProductControllerV16 = true;
  document.documentElement.dataset.svController = 'v16';

  if (!/^\/products\/[^/]+/.test(location.pathname)) return;

  const state = { adding: false, mediaCancelled: false, initialMediaApplied: false, mediaRetryQueued: false };
  let productPromise;
  let initialMediaObserver;
  let initialMediaTimer;

  const getProduct = () =>
    productPromise ||
    (productPromise = fetch(`${location.pathname}.js`, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`Product data ${response.status}`);
      return response.json();
    }));

  const visible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    return (
      rect.width >= 180 &&
      rect.height >= 180 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  };

  const galleryRoots = () => {
    const galleries = Array.from(
      document.querySelectorAll(
        'gp-carousel[type="gp-feature-image-carousel"], gp-carousel-v3[type="gp-feature-image-carousel"]'
      )
    ).filter(visible);

    // GemPages pages can include several decorative carousels below the product.
    // Only the largest visible feature carousel is the product gallery. Updating
    // every carousel caused a late initial-media pass to reset unrelated content.
    galleries.sort((a, b) => {
      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();
      return bRect.width * bRect.height - aRect.width * aRect.height;
    });
    return galleries.slice(0, 1);
  };

  const mediaBasename = (url) => {
    try {
      return decodeURIComponent(new URL(url, location.origin).pathname.split('/').pop() || '').split('?')[0];
    } catch (_) {
      return '';
    }
  };

  const showSlide = (gallery, item, index) => {
    const items = Array.from(gallery.querySelectorAll('.gem-slider-item'));
    if (!item || index < 0 || !items.length) return false;

    gallery.setAttribute('active-index', String(index));
    items.forEach((slide, slideIndex) => {
      slide.classList.toggle('gp-active', slideIndex === index);
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    const track = gallery.querySelector('.gem-slider-wrapper');
    const offset = -(item.offsetLeft || index * item.getBoundingClientRect().width);
    items.forEach((slide) => {
      slide.style.removeProperty('transform');
    });
    // GemPages positions this carousel from the track, not from each slide.
    // Moving individual items can mark slide 0 active while leaving slide 6
    // in the viewport. Apply the position to the actual slider track instead.
    track?.style.setProperty('transform', `translate3d(${offset}px, 0, 0)`, 'important');
    return true;
  };

  const showInitialProductMedia = async () => {
    if (state.mediaCancelled) return false;

    let filename = '';
    try {
      const product = await getProduct();
      filename = mediaBasename(product.featured_image);
    } catch (_) {
      // The first rendered slide remains the safe fallback.
    }

    if (state.mediaCancelled) return false;
    let applied = false;
    for (const gallery of galleryRoots()) {
      const items = Array.from(gallery.querySelectorAll('.gem-slider-item'));
      let targetIndex = -1;
      if (filename) {
        targetIndex = items.findIndex((item) =>
          Array.from(item.querySelectorAll('img')).some(
            (image) => mediaBasename(image.currentSrc || image.src) === filename
          )
        );
      }
      if (targetIndex < 0) targetIndex = 0;
      applied = showSlide(gallery, items[targetIndex], targetIndex) || applied;
    }
    if (applied) state.initialMediaApplied = true;
    return applied;
  };

  const showMediaByFilename = (filename) => {
    if (!filename) return false;
    let changed = false;

    const thumbnail = Array.from(document.querySelectorAll('img')).find((image) => {
      if (mediaBasename(image.currentSrc || image.src) !== filename) return false;
      const carousel = image.closest('gp-carousel, gp-carousel-v3');
      if (!carousel) return false;
      const rect = carousel.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && (rect.width < 180 || rect.height < 180);
    });
    thumbnail?.click();

    for (const gallery of galleryRoots()) {
      const items = Array.from(gallery.querySelectorAll('.gem-slider-item'));
      const targetIndex = items.findIndex((item) =>
        Array.from(item.querySelectorAll('img')).some(
          (image) => mediaBasename(image.currentSrc || image.src) === filename
        )
      );
      if (targetIndex >= 0) changed = showSlide(gallery, items[targetIndex], targetIndex) || changed;
    }
    return changed;
  };

  const syncClickedColorMedia = (button) => {
    const swatchImage = button?.querySelector('img');
    const filename = mediaBasename(swatchImage?.currentSrc || swatchImage?.src || '');
    if (!filename) return;
    showMediaByFilename(filename);
    requestAnimationFrame(() => showMediaByFilename(filename));
    window.setTimeout(() => showMediaByFilename(filename), 80);
    window.setTimeout(() => showMediaByFilename(filename), 220);
  };

  const cancelInitialMediaWork = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const colorButton = target.closest('button[aria-label*="Color"]');
    const productGallery = target.closest(
      'gp-carousel[type="gp-feature-image-carousel"], gp-carousel-v3[type="gp-feature-image-carousel"], .gp-feature-image-wrapper'
    );
    if (productGallery && !colorButton) {
      state.mediaCancelled = true;
      stopInitialMediaWork();
      // A swipe begins with pointerdown. Clearing the inline slide position here
      // briefly returns the carousel to slide zero before GemPages finishes the
      // gesture, which is visible as a flash of the first image. Cancelling the
      // one-time initial-media work is sufficient; preserve the current slide.
      return;
    }
    if (
      target.closest(
        'input[type="radio"], select, variant-picker, [data-option-value], button[aria-label*="Color"]'
      )
    ) {
      state.mediaCancelled = true;
      stopInitialMediaWork();
      for (const gallery of galleryRoots()) {
        gallery.querySelectorAll('.gem-slider-item').forEach((slide) => slide.style.removeProperty('transform'));
      }
      if (colorButton) syncClickedColorMedia(colorButton);
    }
  };

  document.addEventListener('pointerdown', cancelInitialMediaWork, true);
  document.addEventListener('change', cancelInitialMediaWork, true);

  const stopInitialMediaWork = () => {
    if (initialMediaTimer) window.clearTimeout(initialMediaTimer);
    initialMediaTimer = undefined;
    initialMediaObserver?.disconnect();
    initialMediaObserver = undefined;
    state.mediaRetryQueued = false;
  };

  const scheduleInitialMedia = () => {
    const attempt = () => {
      state.mediaRetryQueued = false;
      if (state.mediaCancelled) {
        stopInitialMediaWork();
        return;
      }
      showInitialProductMedia();
    };

    // The carousel is injected asynchronously by GemPages. Observe only during
    // initial hydration and stop as soon as the first product image is applied.
    initialMediaObserver = new MutationObserver(() => {
      if (state.mediaRetryQueued || state.mediaCancelled) return;
      state.mediaRetryQueued = true;
      requestAnimationFrame(attempt);
    });
    initialMediaObserver.observe(document.documentElement, { childList: true, subtree: true });

    [0, 120, 350, 700, 1200, 2200, 3200].forEach((delay) => window.setTimeout(attempt, delay));
    initialMediaTimer = window.setTimeout(stopInitialMediaWork, 4500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialMedia, { once: true });
  } else {
    scheduleInitialMedia();
  }

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

  const normalizeOption = (value) =>
    String(value ?? '')
      .replace(/\s*\([^)]*MOST POPULAR[^)]*\)\s*/gi, '')
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .toLowerCase();

  const selectedVariant = async () => {
    const product = await getProduct();
    const values = {};
    const optionPrefix = `${product.id}-options-`;

    document.querySelectorAll('label.option-item-active input[type="radio"]').forEach((input) => {
      if (!input.name?.startsWith(optionPrefix)) return;
      const optionName = input.name.slice(optionPrefix.length);
      values[optionName] = input.value;
    });

    const activeColor = Array.from(document.querySelectorAll('button[aria-label*="Color"].is-active')).find(
      (button) => button.getClientRects().length
    );
    const colorMatch = activeColor?.getAttribute('aria-label')?.match(/^Color\s+\d+\s+(.+)$/i);
    if (colorMatch) values.Color = colorMatch[1];

    return (
      product.variants.find((variant) =>
        product.options.every(
          (option, index) =>
            values[option.name] == null ||
            normalizeOption(variant.options[index]) === normalizeOption(values[option.name])
        )
      ) || product.variants.find((variant) => variant.available)
    );
  };

  const openNativeDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (typeof drawer?.open === 'function') {
      drawer.open();
      return;
    }
    const dialog = drawer?.querySelector('dialog');
    if (dialog && !dialog.open) dialog.showModal();
  };

  const dispatchCartAdd = (button, response, quantity, variantId) => {
    button.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: response,
          sourceId: button.id || button.dataset.id || 'signalvault-gempages',
          data: {
            source: 'product-form-component',
            itemCount: quantity,
            variantId,
            sections: response.sections || {},
          },
        },
      })
    );

    // Let the native cart listener apply Shopify's returned section first.
    // Two animation frames keep the opening animation and first cart render in
    // the same visual state instead of exposing an empty/previous drawer.
    requestAnimationFrame(() => requestAnimationFrame(openNativeDrawer));
  };

  const addToCart = async (event, button) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (state.adding || button.disabled) return;
    document.documentElement.dataset.svCartStage = 'clicked';
    const form = button.closest('form[action*="/cart/add"]');
    if (!form) {
      document.documentElement.dataset.svCartStage = 'form-missing';
      return;
    }

    state.adding = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // Preserve the existing 3-pack custom colour properties before FormData is read.
      window.__svcpColorManagerV3?.prepare?.(button, form);
      const formData = new FormData(form);
      const variant = await selectedVariant();
      const variantId = String(variant?.id || formData.get('id') || '');
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      if (!/^\d+$/.test(variantId)) throw new Error('Selected product option is unavailable.');
      document.documentElement.dataset.svCartVariant = variantId;
      formData.set('id', variantId);
      formData.set('quantity', String(quantity));

      const sections = cartSectionIds();
      if (sections.length) formData.set('sections', sections.join(','));
      formData.set('sections_url', location.pathname);

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }
      document.documentElement.dataset.svCartStage = 'added';
      dispatchCartAdd(button, result, quantity, variantId);
    } catch (error) {
      document.documentElement.dataset.svCartStage = 'error';
      document.documentElement.dataset.svCartError = String(error?.message || error);
      console.error('[SignalVault cart]', error);
      window.alert(error?.message || 'Unable to add this item. Please try again.');
    } finally {
      state.adding = false;
      button.removeAttribute('aria-busy');
    }
  };

  window.__svHandleAdd = addToCart;

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('.gp-button-atc') : null;
      if (button) addToCart(event, button);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;
      const button = event.submitter?.matches?.('.gp-button-atc')
        ? event.submitter
        : form.querySelector('.gp-button-atc');
      if (button) addToCart(event, button);
    },
    true
  );
})();
